/**
 * Krok UXV4b — „Nový inzerát" (`/v1/cases/:caseId/listing-draft*`).
 *
 * Tvary odpovedi jsou zrcadlem `ListingDraftView` / `ListingDraftPublishResult`
 * z `@reos/listing` (web-admin kontexty neimportuje). Server vraci jen kody
 * (puvod, kontrola, pole portalu); cesky text sklada `listing-draft-format.ts`.
 */
import { baseUrl } from './api.js';
import { DomainApiError } from './api-error.js';

export type DraftOrigin = 'cadastre' | 'ruian' | 'penb' | 'hoa' | 'document' | 'owner' | 'listing' | 'broker';

export type DraftValueType =
  | 'text'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'energy_class'
  | 'ownership'
  | 'structure'
  | 'condition'
  // Krok UXP5 bod 1: druh domu (`house_kind` ze serveru, vycet `HOUSE_KIND_VALUES`).
  | 'house_kind'
  // Krok APP2 bod 4: druh pozemku (`land_kind` ze serveru, vycet `LAND_KIND_VALUES`).
  | 'land_kind'
  | 'hoa'
  | 'money'
  | 'date_or_now';

export interface DraftFieldView {
  readonly key: string;
  readonly valueType: DraftValueType;
  readonly unit: 'm2' | null;
  readonly main: boolean;
  readonly listed: boolean;
  readonly value: unknown;
  readonly origin: DraftOrigin | null;
  readonly official: boolean;
  readonly conflict: boolean;
  readonly overridden: boolean;
  readonly alternatives: readonly { readonly value: unknown; readonly origin: DraftOrigin }[];
  readonly options: readonly string[] | null;
  readonly editable: boolean;
}

export type DraftCheck =
  | { readonly code: 'matches_cadastre'; readonly severity: 'ok' }
  | {
      readonly code: 'area_mismatch';
      readonly severity: 'warning';
      readonly official: number;
      readonly claimed: number;
      readonly claimedOrigin: DraftOrigin;
    }
  | {
      readonly code: 'disposition_mismatch';
      readonly severity: 'warning';
      readonly official: string;
      readonly claimed: string;
      readonly claimedOrigin: DraftOrigin;
    }
  | { readonly code: 'lien_escrow'; readonly severity: 'info' }
  | { readonly code: 'energy_class_missing'; readonly severity: 'blocker' }
  | { readonly code: 'price_missing'; readonly severity: 'blocker' }
  /**
   * Krok PROP3 bod 4: zverejneny inzerat vychazi z udaje, ktery se po odeslani
   * zmenil („Údaje se změnily — aktualizovat na portálech"). Jen upozorneni,
   * na portaly se nic neposila samo.
   */
  | { readonly code: 'facts_changed'; readonly severity: 'warning'; readonly fields: readonly string[] }
  /**
   * Krok NAP1 bod 1 (kontrola AI5): inzerát má adresu, ale souhlas majitele
   * s přesnou polohou chybí — na portály jde přibližná poloha (ulice/obec).
   */
  | { readonly code: 'precise_location_consent_missing'; readonly severity: 'warning' }
  /**
   * Krok PORT14: nadpis je delší, než unese nejstriktnější z napojených
   * portálů (Bazoš 60 znaků). Jen upozornění u náhledu exportu — na portály
   * s delším limitem inzerát jde v pořádku. Čísla posílá server
   * (`LISTING_TITLE_MAX_LENGTH`), admin si limit NEOPISUJE.
   */
  | {
      readonly code: 'title_too_long_for_portal';
      readonly severity: 'warning';
      readonly limit: number;
      readonly length: number;
    };

export type DraftPortalId = 'sreality' | 'reality_idnes' | 'realitymix' | 'ceskereality' | 'bazos';

export interface DraftPortalView {
  readonly id: DraftPortalId;
  readonly availability: 'connected' | 'preparing';
  readonly note: 'office_account' | 'free' | 'credit';
  readonly selected: boolean;
  readonly publicationState: string | null;
  readonly lastErrorCode: string | null;
}

export type EnvPortalMode = 'DEMO' | 'TEST' | 'LIVE';

export interface ListingDraftView {
  readonly caseId: string;
  readonly propertyId: string | null;
  readonly propertyKind: string;
  readonly addressText: string | null;
  readonly reviewDone: boolean;
  readonly title: string;
  readonly filledCount: number;
  readonly officialOrigins: readonly DraftOrigin[];
  readonly fields: readonly DraftFieldView[];
  readonly fill: readonly DraftFieldView[];
  /**
   * Krok PROP3B bod 1: predstava majitele (`sale.owner_price_expectation`)
   * pro vyzvu u pole „Cena". Nabidkovou cenou se stane jen klepnutim.
   */
  readonly ownerPriceExpectation: { readonly amount: string; readonly currency: string } | null;
  readonly checks: readonly DraftCheck[];
  /**
   * Krok NAP1 bod 1 — kontroly toho, CO ODESÍLÁME (souhlas majitele
   * s přesnou polohou). Patří do náhledu exportu; starší server pole
   * neposílá, proto nepovinné.
   */
  readonly exportWarnings?: readonly DraftCheck[] | undefined;
  /**
   * Krok PORT6: `maxLength` je limit popisu z napojených portálů (Sreality
   * potvrdila 5000 znaků 2. 11. 2026, nejkratší limit drží server). Starší
   * server ho neposílá, proto nepovinné — bez něj se počítadlo nekreslí.
   */
  readonly text: {
    readonly value: string;
    readonly source: 'template' | 'ai' | 'broker';
    readonly aiAvailable: boolean;
    readonly maxLength?: number | undefined;
  };
  readonly photos: readonly { readonly id: string; readonly url: string | null; readonly caption: string | null }[];
  readonly portals: readonly DraftPortalView[];
  readonly envMode: EnvPortalMode;
  readonly status: 'draft' | 'submitted';
  readonly submittedAt: string | null;
  /**
   * Krok NAP1 bod 1 — „Zobrazit přesnou adresu" (souhlas majitele).
   * Nepovinné: starší server tohle pole neposílá a chová se jako `false`.
   */
  readonly showExactAddress?: boolean | undefined;
}

export type PortalIssueCode =
  | 'missing'
  | 'photos_too_few'
  | 'photos_too_many'
  | 'photo_too_small'
  | 'text_too_long'
  | 'unsupported_kind'
  | 'other';

export interface PortalIssue {
  readonly portal: DraftPortalId;
  readonly code: PortalIssueCode;
  readonly field: string;
  readonly context?: Readonly<Record<string, string | number>> | undefined;
}

export type DispatchAction =
  | 'skip_off'
  | 'skip_preparing'
  | 'preview_only'
  | 'send_test'
  | 'live_awaiting_go'
  /** P1EXP1: ostry export kancelare je zapnuty, inzerat jde na ostry portal. */
  | 'send_live';
export type DispatchReason =
  | 'switched_off'
  | 'portal_preparing'
  | 'demo_mode'
  | 'test_connection_missing'
  | 'live_key_missing'
  | 'live_not_enabled'
  | 'ready'
  | 'ready_live';

export interface PublishDecision {
  readonly portal: DraftPortalId;
  readonly action: DispatchAction;
  readonly reason: DispatchReason;
  readonly publicationId: string | null;
  readonly state: string | null;
  readonly problem: string | null;
}

export interface PublishResult {
  readonly ok: boolean;
  readonly blockingChecks: readonly DraftCheck[];
  readonly missing: readonly string[];
  readonly portalIssues: readonly PortalIssue[];
  readonly decisions: readonly PublishDecision[];
  readonly view: ListingDraftView;
}

export interface DraftPatch {
  readonly overrides?: Readonly<Record<string, unknown>>;
  readonly text?: string | null;
  readonly portals?: Readonly<Record<string, boolean>>;
  /** Krok NAP1 bod 1 — souhlas majitele s přesnou adresou na portálech. */
  readonly showExactAddress?: boolean;
}

interface ProblemBody {
  readonly title?: string;
  readonly detail?: string;
  readonly type?: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    headers: { accept: 'application/json', ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}) },
    ...init,
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as ProblemBody | null;
    // `type` nese strojovy kod (`listing-draft/ai-unavailable`), `detail` pole s chybou.
    throw new DomainApiError(problem?.title ?? `HTTP ${String(res.status)}`, res.status, problem?.type);
  }
  return (await res.json()) as T;
}

const path = (caseId: string): string => `/v1/cases/${encodeURIComponent(caseId)}/listing-draft`;

export function fetchListingDraft(caseId: string): Promise<ListingDraftView> {
  return request(path(caseId));
}

export function fetchListingDraftSummary(caseId: string): Promise<{ readonly state: 'none' | 'draft' | 'submitted' }> {
  return request(`${path(caseId)}/summary`);
}

export function saveListingDraft(caseId: string, patch: DraftPatch): Promise<ListingDraftView> {
  return request(path(caseId), { method: 'PATCH', body: JSON.stringify(patch) });
}

export function generateListingText(caseId: string, mode: 'template' | 'ai'): Promise<ListingDraftView> {
  return request(`${path(caseId)}/text`, { method: 'POST', body: JSON.stringify({ mode }) });
}

export interface ListingDescriptionDraft {
  readonly text: string | null;
  /**
   * Krok NAB5 bod 3 — varianty popisu (makléř jednu vybere a upraví).
   * Nepovinné: starší server vrací jen `text`.
   */
  readonly texts?: readonly string[] | undefined;
  readonly flagged: boolean;
  readonly needsReviewNumbers: readonly string[];
}

/** Krok UI1 — „Navrhnout popis" (AI, SR4): návrh do pole, makléř upraví/potvrdí. */
export function generateListingDescription(caseId: string): Promise<ListingDescriptionDraft> {
  return request(`${path(caseId)}/description`, { method: 'POST', body: '{}' });
}

/** Krok NAP1 bod 2 — jazyky překladu inzerátu (`LISTING_TRANSLATION_LANGUAGES` v @reos/ai). */
export type ListingTranslationLanguage = 'en' | 'de' | 'uk' | 'ru';

export interface ListingTranslation {
  /** `null` = překlad neprošel kontrolou čísel; nic se nepoužije. */
  readonly text: string | null;
  readonly language: ListingTranslationLanguage;
  readonly numbersPreserved: boolean;
  readonly missingNumbers: readonly string[];
  readonly addedNumbers: readonly string[];
}

/** Krok NAP1 bod 2 — „Přeložit" (AI). Výsledek jen do pole, na portál nic nejde. */
export function translateListingDraft(caseId: string, language: ListingTranslationLanguage): Promise<ListingTranslation> {
  return request(`${path(caseId)}/translate`, { method: 'POST', body: JSON.stringify({ language }) });
}

/** Krok NAP1 bod 3 — „Text pro sítě" (deterministický střih údajů inzerátu). */
export function fetchListingSocialCaption(caseId: string): Promise<{ readonly caption: string }> {
  return request(`${path(caseId)}/social-caption`);
}

export function publishListingDraft(caseId: string): Promise<PublishResult> {
  return request(`${path(caseId)}/publish`, { method: 'POST', body: '{}' });
}

/*
 * Krok UI1b — KRÁTKÉ VIDEO Z FOTEK.
 *
 * Render běží ve frontě workera (ffmpeg je desítky sekund), takže API vrací
 * STAV a obrazovka se dotahuje, dokud není `DONE`. Stažení je samostatná
 * cesta (`downloadPath`) — nikam se nic nezveřejňuje, soubor si bere makléř.
 */
export type ListingVideoOrientation = 'PORTRAIT' | 'LANDSCAPE';

/** Krok VID2 — nálada hudby k videu (jen Pixabay, viz @reos/listing). */
export type ListingVideoMusicMood = 'KLIDNA' | 'SVIZNA' | 'ELEGANTNI';

export interface ListingVideo {
  readonly id: string;
  readonly status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';
  readonly orientation: ListingVideoOrientation;
  /** `null` = bez hudby. */
  readonly musicMood: ListingVideoMusicMood | null;
  readonly createdAt: string;
  readonly durationMs: number | null;
  readonly widthPx: number | null;
  readonly heightPx: number | null;
  readonly bytes: number | null;
  readonly photoCount: number | null;
  readonly failureReason: string | null;
  /** `null` dokud video není hotové. */
  readonly downloadPath: string | null;
}

export interface ListingVideoList {
  readonly items: readonly ListingVideo[];
  /** Kolik videí kancelář dnes ještě smí (limit za den). */
  readonly remainingToday: number;
  /** Nálady, pro které knihovna hudby má aspoň jednu skladbu. */
  readonly availableMoods: readonly ListingVideoMusicMood[];
}

const videoPath = (caseId: string): string => `/v1/cases/${encodeURIComponent(caseId)}/listing-video`;

export function fetchListingVideos(caseId: string): Promise<ListingVideoList> {
  return request(videoPath(caseId));
}

export function createListingVideo(
  caseId: string,
  orientation: ListingVideoOrientation,
  musicMood: ListingVideoMusicMood | null = null,
): Promise<ListingVideo> {
  return request(videoPath(caseId), { method: 'POST', body: JSON.stringify({ orientation, musicMood }) });
}
